import React from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import Cookie from 'js-cookie';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import without from 'lodash/without';
import startsWith from 'lodash/startsWith';
import { withRouter } from 'react-router';

import Dropdown from '../../components/Dropdown';
import Icon from '../../components/Icon';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Confirm from '../../components/Confirm';
import Popup from '../../components/Popup';
import Form from '../../components/Form';
import Ref from '../../components/Ref';
import { ADMIN_ROLE, PUBLISHING_ROLE, COMMUNITY_ROLE } from '../Authorization/constants';
import HelpLink from '../HelpLink';
import { dropdownImg } from './styles.css';

import mailImg from './mail.svg';

import { logout } from '../Authorization/actions';
import { onHEAccountSubscription } from '../../utils/subscriptions';
import { reload } from '../../utils';

const communityRoles = [ADMIN_ROLE, PUBLISHING_ROLE, COMMUNITY_ROLE];

const messages = defineMessages({
  user: {
    id: 'user.dropdown.user',
    defaultMessage: 'Account Settings',
  },
  yourProfile: {
    id: 'user.dropdown.yourProfile',
    defaultMessage: 'Your Profile',
  },
  institutionProfile: {
    id: 'user.dropdown.institution',
    defaultMessage: 'Institution Profile',
  },
  updateProfile: {
    id: 'user.dropdown.updateProfile',
    defaultMessage: 'Update Profile',
  },
  userDropdown: {
    id: 'user.dropdown',
    defaultMessage: 'User Dropdown',
  },
  contactSupport: {
    id: 'user.dropdown.contactSupport',
    defaultMessage: 'Contact Support',
  },
  loggedInAs: {
    id: 'user.dropdown.loggedInAs',
    defaultMessage: 'Logged in as {currentUser}',
  },
  isAdmin: {
    id: 'user.dropdown.isAdmin',
    defaultMessage: 'ADMIN',
  },
  signout: {
    id: 'user.dropdown.signout',
    defaultMessage: 'Sign Out',
  },
});
class UserInfo extends React.PureComponent {
  static propTypes = {
    router: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    current: PropTypes.shape({
      viewer: PropTypes.object.isRequired,
    }).isRequired,
    logout: PropTypes.func.isRequired,
    mutateAddFeatureFlag: PropTypes.func.isRequired,
    mutateRemoveFeatureFlag: PropTypes.func.isRequired,
    viewer: PropTypes.shape({
      enabledFeatures: PropTypes.array.isRequired,
      globallyEnabledFeatures: PropTypes.array.isRequired,
      uuid: PropTypes.string.isRequired,
      heInstitution: PropTypes.string.isRequired,
    }).isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
  };

  state = {
    isError: false,
    requesting: false,
  };

  onChange = (e, { value }) => {
    const { viewer: { enabledFeatures, uuid, heInstitution } } = this.props;
    const isCommunityServerlessOn = enabledFeatures.includes('COMMUNITYSERVERLESS');
    const profileUrl = isCommunityServerlessOn ? `/counselorcommunity/search/people/${uuid}` : '/counselor-community/profile';
    const institutionUrl = isCommunityServerlessOn ? `/counselorcommunity/search/institutions/colleges/${heInstitution}` : '/counselor-community/institution';

    const { router, viewer, current } = this.props;
    /* istanbul ignore else */
    if (value === 'signout') {
      // TODO: Remove this when sync with the backend
      const googleAuthInstance = window.gapi ? window.gapi.auth2.getAuthInstance() : null;
      if (googleAuthInstance && googleAuthInstance.isSignedIn.get()) {
        // disconnect google calendar
        googleAuthInstance.disconnect();
        googleAuthInstance.signOut();
      }
      this.props.logout(current.viewer);
    } else if (value === 'user') {
      router.push('/settings');
    } else if (value === 'updateProfile') {
      router.push(profileUrl);
      window.dispatchEvent(new Event('counselor-community-serverless-update-route'));
    } else if (value === 'institution') {
      router.push(institutionUrl);
      window.dispatchEvent(new Event('counselor-community-serverless-update-route'));
    } else if (value === 'contactSupport') {
      window.location.assign(`mailto:${process.env.SUPPORT_EMAIL}`);
    } else if (value === 'addFeatureFlag' || value === 'globalFeatures') {
      e.stopPropagation();
      /* istanbul ignore else */
    } else if (typeof value === 'string' && startsWith(value, 'feature:')) {
      const feature = value.substring('feature:'.length);
      /* istanbul ignore else */
      if (!viewer.globallyEnabledFeatures.includes(feature)) {
        this.setState({ removeFeatureFlag: feature });
      }
      e.stopPropagation();
    }
  };

  onAddFeatureKeyPress = (e) => {
    if (e.key === 'Enter') {
      this.addFeatureFlag(e.target);
      e.target.value = '';
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  onAddFeatureSubmit = (e) => {
    this.addFeatureFlag(e.target.elements[0]);
    e.target.elements[0].value = '';
  };

  onFailure = (transaction) => {
    this.setState({ isError: true, requesting: false });
  };

  onSuccess = () => {
    this.setState({ isError: false, requesting: false });
  };

  switchBranch = () => {
    if (Cookie.get('x-source')) {
      Cookie.remove('x-source');
    } else {
      Cookie.set('x-source', 'release');
    }
    reload();
  };

  getSourceCookieDropdownItem(sourceCookie) {
    return (
      <Dropdown.Item value="branch" key="branch" onClick={this.switchBranch}>
        <Icon name="code branch" />
        {sourceCookie || process.env.BUILDBRANCH}
      </Dropdown.Item>
    );
  }

  getFeatureToggleDropdownItems() {
    const { viewer: { enabledFeatures, globallyEnabledFeatures } } = this.props;
    const { isError, requesting } = this.state;
    return [
      <Dropdown.Item value="addFeatureFlag" key="addFeatureFlag" onClick={this.onChange}>
        <Form onSubmit={this.onAddFeatureSubmit}>
          <Form.Group styleName="add-feature-group">
            <Input action error={isError}>
              <input placeholder="Add Feature Flag" aria-label="Add Feature Flag" onKeyDown={this.onAddFeatureKeyPress} />
              <Button loading={requesting} aria-label="Add Feature Flag" color="teal" type="submit" icon><Icon name="plus" /></Button>
            </Input>
          </Form.Group>
        </Form>
      </Dropdown.Item>,
      <Dropdown.Item value="globalFeatures" key="globalFeatures" onClick={this.onChange}>
        <Icon name="globe" />
        <Popup trigger={<span styleName="global-features-overflow">{globallyEnabledFeatures.join(', ')}</span>}>
          {globallyEnabledFeatures.map((feature) => (<div key={feature}>{feature}</div>))}
        </Popup>
      </Dropdown.Item>,
      ...without(enabledFeatures, ...globallyEnabledFeatures).map((feature) => {
        const featureKey = `feature:${feature}`;
        return <Dropdown.Item value={featureKey} key={featureKey} text={feature} icon="flag" onClick={this.onChange} />;
      }),
    ];
  }

  getUserNameFromViewer() {
    const { current: { viewer } } = this.props;

    let userName = get(viewer, 'idToken.name');
    if (userName === undefined) {
      const givenName = get(viewer, 'idToken.given_name');
      const familyName = get(viewer, 'idToken.family_name');
      userName = (givenName !== undefined) ? givenName : '';
      if (userName.length > 0 && familyName !== undefined && familyName.length > 0) {
        userName += ' ';
      }
      userName += (familyName !== undefined) ? familyName : '';
    }
    return userName;
  }

  addFeatureFlag(e) {
    if (!e.value) {
      return;
    }
    this.setState({ isError: false, requesting: true });
    const { onFailure, onSuccess } = this;
    this.props.mutateAddFeatureFlag({
      variables: {
        input: {
          featureFlag: e.value,
        },
      },
    }).then(onSuccess, onFailure);
  }

  removeFeatureFlag(featureFlag) {
    this.setState({ isError: false, requesting: true });
    const { onFailure, onSuccess } = this;
    this.props.mutateRemoveFeatureFlag({
      variables: {
        input: {
          featureFlag,
        },
      },
    }).then(onSuccess, onFailure);
  }

  handleRemoveCancel = () => {
    this.setState({ removeFeatureFlag: '' });
    /* istanbul ignore else */
    if (this.dropdownRef) {
      this.dropdownRef.focus();
    }
  };

  handleRemoveConfirm = () => {
    const { removeFeatureFlag } = this.state;
    this.removeFeatureFlag(removeFeatureFlag);
    this.setState({ removeFeatureFlag: '' });
    /* istanbul ignore else */
    if (this.dropdownRef) {
      this.dropdownRef.focus();
    }
  };

  handleRef = (ref) => {
    this.dropdownRef = ref;
  };

  isAdmin() {
    const { current: { viewer } } = this.props;
    return (get(viewer, 'idToken["custom:role"]') === ADMIN_ROLE);
  }

  getDropdownOptions() {
    const { viewer, current, intl } = this.props;
    const sourceCookie = Cookie.get('x-source');
    const ddOptionAccountSettings = {
      icon: 'cog',
      id: 'user-dropdown-change-profile',
      value: 'user',
      text: intl.formatMessage(messages.user),
    };

    const ddOptionYourProfile = {
      icon: 'user',
      id: 'user-dropdown-update-profile',
      value: 'updateProfile',
      text: intl.formatMessage(messages.yourProfile),
    };

    const ddOptionInstitutionProfile = {
      icon: 'users',
      id: 'user-dropdown-institution-profile',
      value: 'institution',
      text: intl.formatMessage(messages.institutionProfile),
    };

    const ddOptionContactSupport = {
      image: { 'src': mailImg, 'alt': '', 'aria-hidden': true, 'className': dropdownImg },
      id: 'user-dropdown-contact-support',
      value: 'contactSupport',
      text: intl.formatMessage(messages.contactSupport),
    };

    const ddOptionLoggedInAs = {
      id: 'user-dropdown-loggedInAs',
      value: 'loggedInAs',
      disabled: true,
      className: 'user-dropdown-info-disabled',
      children: (
        <span>
          <span styleName="user-dropdown-name">
            {intl.formatMessage(messages.loggedInAs, { currentUser: this.getUserNameFromViewer() })}
          </span>
          {this.isAdmin() && <span styleName="user-dropdown-is-admin">{intl.formatMessage(messages.isAdmin)}</span>}
        </span>
      ),
    };

    const ddOptionSignout = {
      icon: 'sign-out',
      id: 'user-dropdown-signout',
      value: 'signout',
      text: intl.formatMessage(messages.signout),
    };

    const getSupportLink = function getSupportLink(isFreemium) {
      const email = `mailto:${isFreemium ? process.env.COUNSELOR_COMMUNITY_EMAIL : process.env.SUPPORT_EMAIL}`;
      return function predicate() {
        return (
          <Dropdown.Item
            onClick={/* istanbul ignore next */ () => window.location.assign(email)}
            key="help-nav-menu-contactsupport"
            value="contactSupport"
            className="hidden-computer hidden-tablet"
          >
            <FormattedMessage {...messages.contactSupport} />
          </Dropdown.Item>
        );
      };
    };

    const ContactSupport =
      onHEAccountSubscription((heAccount) => heAccount.freemium, { failure: getSupportLink(false) })(getSupportLink(true));

    let noCommunityDDOptions;
    let communityDDOptions;

    if (current.viewer.impersonator) {
      noCommunityDDOptions = [ddOptionSignout];
      communityDDOptions = [ddOptionContactSupport, ddOptionLoggedInAs, ddOptionSignout];
    } else {
      noCommunityDDOptions = [ddOptionAccountSettings, ddOptionSignout];
      communityDDOptions = [
        ddOptionAccountSettings,
        <Dropdown.Divider key="divider1" />,
        ddOptionYourProfile,
        ddOptionInstitutionProfile,
        <Dropdown.Divider key="divider2" className="hidden-computer hidden-tablet" />,
        <Dropdown.Item key="helpLink" className="hidden-computer hidden-tablet">
          <HelpLink idPrefix="helpUserMenu" />
        </Dropdown.Item>,
        <ContactSupport key="contactSupport" />,
        <Dropdown.Divider key="divider3" />,
        ddOptionLoggedInAs,
        ddOptionSignout,
      ];
    }
    const viewerRole = get(current, 'viewer.idToken["custom:role"]');
    const dropdownOptions = communityRoles.includes(viewerRole) ? communityDDOptions : noCommunityDDOptions;
    if (viewer.enabledFeatures.includes('ENABLE_FEATURE_TOGGLER')) {
      dropdownOptions.push(...this.getFeatureToggleDropdownItems());
    }
    if (viewer.enabledFeatures.includes('BRANCH_TOGGLER') || sourceCookie) {
      dropdownOptions.unshift(this.getSourceCookieDropdownItem(sourceCookie));
    }
    return dropdownOptions;
  }

  render() {
    const { intl } = this.props;
    const { removeFeatureFlag } = this.state;
    const sourceCookie = Cookie.get('x-source');
    const userAlert = sourceCookie ?
      (<Icon name="code branch" circular inverted color="pink" size="small" styleName="alertBubble" />) : null;
    const trigger = (<Icon name="user circle" size="big" styleName="avatarNav" />);

    const dropdownOptions = this.getDropdownOptions();
    return (
      <span>
        {removeFeatureFlag && (
        <Confirm
          open
          header="Remove Feature Flag?"
          content={`Are you sure you want to remove the ${removeFeatureFlag} feature flag?`}
          onCancel={this.handleRemoveCancel}
          onConfirm={this.handleRemoveConfirm}
        />
        )}
        <Ref innerRef={this.handleRef}>
          <Dropdown
            value="none"
            aria-label={intl.formatMessage(messages.userDropdown)}
            selectOnBlur={false}
            selectOnNavigation={false}
            id="user-dropdown"
            onChange={this.onChange}
            className="top right dropdownMenu"
            floating
            styleName="dropDownNav"
            options={dropdownOptions}
            icon={userAlert}
            trigger={trigger}
          />
        </Ref>
      </span>
    );
  }
}

const mapStateToProps = (state) => ({
  current: state.current,
});

export const UserInfoComponent = injectIntl(UserInfo);

export default withRouter(connect(mapStateToProps, { logout })(UserInfoComponent));
